import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Filter, RefreshCcw, ChevronDown, ChevronUp } from "lucide-react";

const ACTION_CLR = {
  delete: "bg-[#FEE2E2] text-[#B91C1C]",
  update: "bg-[#FEF3C7] text-[#B45309]",
  create: "bg-[#DCFCE7] text-[#15803D]",
  verify: "bg-[#DBEAFE] text-[#1D4ED8]",
  unverify: "bg-gray-100 text-gray-700",
  log: "bg-[#F3E8FF] text-[#7E22CE]",
  update_notes: "bg-[#FEF3C7] text-[#B45309]",
};

const timeOf = (ts) => {
  if (!ts) return "";
  try { return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (entity) params.entity_type = entity;
      if (action) params.action = action;
      const { data } = await api.get("/audit-logs", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [entity, action]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.user_email || "").toLowerCase().includes(s) ||
      (r.entity_type || "").toLowerCase().includes(s) ||
      (r.entity_id || "").toLowerCase().includes(s) ||
      (r.action || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const entityOpts = useMemo(() => Array.from(new Set(rows.map((r) => r.entity_type).filter(Boolean))).sort(), [rows]);
  const actionOpts = useMemo(() => Array.from(new Set(rows.map((r) => r.action).filter(Boolean))).sort(), [rows]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-heading font-black text-2xl tracking-tight">Audit Log</h2>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Every create · update · delete · verify — with before / after snapshots
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="audit-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <select className="input-lg h-9 w-40" value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="audit-entity">
          <option value="">All entities</option>
          {entityOpts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="input-lg h-9 w-36" value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action">
          <option value="">All actions</option>
          {actionOpts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-lg h-9 w-56 pl-8" placeholder="Search user / entity / id…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" />
        </div>
        <span className="text-xs text-gray-500 ml-2">{filtered.length} events</span>
      </div>

      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left w-40">When</th>
              <th className="text-left">User</th>
              <th className="text-left">Action</th>
              <th className="text-left">Entity</th>
              <th className="text-left">Id</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="6" className="py-8 text-center text-gray-400 text-sm">No audit events for this filter</td></tr>
            )}
            {!loading && filtered.map((r) => {
              const eid = r.entity_id || "";
              const key = r.id || `${eid}-${r.timestamp || Math.random()}`;
              const open = !!expanded[key];
              const cls = ACTION_CLR[r.action] || "bg-gray-100 text-gray-700";
              return (
                <React.Fragment key={key}>
                  <tr data-testid={`audit-row-${key}`}>
                    <td className="data-num text-xs">
                      <div>{r.timestamp ? formatDate(r.timestamp) : "—"}</div>
                      <div className="text-gray-400">{timeOf(r.timestamp)}</div>
                    </td>
                    <td className="text-sm">{r.user_email || <span className="text-gray-400">—</span>}</td>
                    <td>
                      <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold capitalize ${cls}`}>{r.action || "?"}</span>
                    </td>
                    <td className="text-sm">{r.entity_type || <span className="text-gray-400">—</span>}</td>
                    <td className="font-mono text-xs text-gray-500">{eid ? eid.slice(0, 8) : <span className="text-gray-400">—</span>}</td>
                    <td className="text-right">
                      {(r.before || r.after) && (
                        <button onClick={() => setExpanded((m) => ({ ...m, [key]: !open }))}
                          className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1"
                          data-testid={`audit-expand-${key}`}>
                          {open ? <>Hide <ChevronUp size={12} /></> : <>Diff <ChevronDown size={12} /></>}
                        </button>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan="6" className="bg-gray-50 py-3 px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <div className="small-label text-gray-500 mb-1">Before</div>
                            <pre className="text-[11px] leading-snug bg-white border border-gray-200 p-2 rounded-sm max-h-64 overflow-auto whitespace-pre-wrap break-all">
{r.before ? JSON.stringify(r.before, null, 2) : "—"}
                            </pre>
                          </div>
                          <div>
                            <div className="small-label text-gray-500 mb-1">After</div>
                            <pre className="text-[11px] leading-snug bg-white border border-gray-200 p-2 rounded-sm max-h-64 overflow-auto whitespace-pre-wrap break-all">
{r.after ? JSON.stringify(r.after, null, 2) : "—"}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Filter size={0} />
    </div>
  );
}
